/* eslint-disable import/extensions */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Modal from "@material-ui/core/Modal";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { useSnackbar } from "notistack";
import { Typography, Card, CardContent, Box } from "@material-ui/core";
import AppLoader from "components/AppLoader/Index";
import {
    generateApiKeysApiCall,
    getApiKeysApiCall,
    regenerateApiKeyApiCall,
    viewApiKeyApiCall,
} from "../../actions/Operations";

const useStyles = makeStyles((theme) => ({
    paper: {
        position: "absolute",
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(4),
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: 8,
        maxWidth: 700,
        width: "90%",
        maxHeight: "80vh",
        overflowY: "auto",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    title: {
        fontSize: "20px",
        fontWeight: "bold",
    },
    closeButton: {
        padding: 0,
    },
    keyCard: {
        marginBottom: theme.spacing(3),
        border: `1px solid ${theme.palette.divider}`,
    },
    cardHeader: {
        fontWeight: "bold",
        marginBottom: theme.spacing(2),
        fontSize: "16px",
    },
    keyDisplay: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
        marginBottom: theme.spacing(2),
        fontFamily: "monospace",
        wordBreak: "break-all",
        minHeight: "40px",
        display: "flex",
        alignItems: "center",
    },
    keyDisplayRevealed: {
        backgroundColor: theme.palette.warning.light,
    },
    emptyState: {
        color: theme.palette.text.secondary,
        fontStyle: "italic",
        paddingBottom: "12px",
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        marginBottom: theme.spacing(2),
    },
    button: {
        textTransform: "none",
    },
    cardContent: {
        paddingBottom: theme.spacing(2),
    },
    confirmDialog: {
        "& .MuiDialog-paper": {
            minWidth: "400px",
        },
    },
    warningText: {
        color: theme.palette.error.main,
        fontWeight: "bold",
    },
}));

const ApiKeyModal = ({
    open,
    onClose,
    Bussines_id,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Main API Key state
    const [mainKeyStatus, setMainKeyStatus] = useState({
        exists: false,
        isSeen: false,
        isRevealed: false,
        valueRedacted: "",
        valueFullTemporary: "",
    });

    // Alternate API Key state
    const [altKeyStatus, setAltKeyStatus] = useState({
        exists: false,
        isSeen: false,
        isRevealed: false,
        valueRedacted: "",
        valueFullTemporary: "",
    });

    const [loading, setLoading] = useState(false);
    const [keyLoading, setKeyLoading] = useState({
        main: null,
        alternate: null,
    });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // "main" or "alternate"

    const setKeyActionLoading = (type, action) => {
        setKeyLoading((previous) => ({
            ...previous,
            [type]: action,
        }));
    };

    const getApiKeyEntry = (response, type) => {
        const apiKeyType = type === "main" ? "MAIN" : "ALTERNATE";
        const data = response?.data;
        const apiKeys = data?.apiKeys;
        if (Array.isArray(apiKeys)) {
            return apiKeys.find((key) => key.apiKeyType === apiKeyType);
        }
        if (
            apiKeys &&
            apiKeys.apiKeyType === apiKeyType &&
            apiKeys.valueRedacted
        ) {
            return apiKeys;
        }
        if (data?.apiKeyType === apiKeyType && data?.valueRedacted) {
            return data;
        }
        return undefined;
    };

    const updateSingleKeyState = (type, response) => {
        const keyEntry = getApiKeyEntry(response, type);
        const updateState = (previous) => ({
            ...previous,
            exists: Boolean(keyEntry),
            isSeen: keyEntry?.valueSeen === true,
            isRevealed: false,
            valueRedacted: keyEntry?.valueRedacted || "",
            valueFullTemporary: "",
        });

        if (type === "main") {
            setMainKeyStatus(updateState);
        } else {
            setAltKeyStatus(updateState);
        }
    };

    const updateKeyStates = (response) => {
        const data = response?.data;
        const apiKeys = Array.isArray(data?.apiKeys) ? data.apiKeys : [];
        const mainKey = apiKeys.find((key) => key.apiKeyType === "MAIN");
        const alternateKey = apiKeys.find(
            (key) => key.apiKeyType === "ALTERNATE"
        );

        setMainKeyStatus((previous) => ({
            ...previous,
            exists: Boolean(mainKey),
            isSeen: mainKey?.valueSeen === true,
            isRevealed: false,
            valueRedacted: mainKey?.valueRedacted || "",
            valueFullTemporary: "",
        }));
        setAltKeyStatus((previous) => ({
            ...previous,
            exists: Boolean(alternateKey),
            isSeen: alternateKey?.valueSeen === true,
            isRevealed: false,
            valueRedacted: alternateKey?.valueRedacted || "",
            valueFullTemporary: "",
        }));
    };

    const handleOperationError = (error, type) => {
        setLoading(false);
        if (type) {
            setKeyActionLoading(type, null);
        }
        enqueueSnackbar(error?.message || "Unable to update API Key.", {
            variant: "error",
        });
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        setLoading(true);
        dispatch(
            getApiKeysApiCall(
                { businessId: Bussines_id },
                (response) => {
                    updateKeyStates(response);
                    setLoading(false);
                },
                handleOperationError
            )
        );

        return undefined;
    }, [Bussines_id, dispatch, open]);

    // Handler: Generate API Key
    const handleGenerate = (type) => {
        setKeyActionLoading(type, "generate");
        dispatch(
            generateApiKeysApiCall(
                { businessId: Bussines_id, apiKeyType: type },
                (response) => {
                    updateSingleKeyState(type, response);
                    setKeyActionLoading(type, null);
                    enqueueSnackbar("API Key generated.", { variant: "success" });
                },
                (error) => handleOperationError(error, type)
            )
        );
    };

    // Handler: View/Reveal API Key
    const handleView = (type) => {
        setKeyActionLoading(type, "view");
        dispatch(
            viewApiKeyApiCall(
                { apiKeyType: type, businessId: Bussines_id },
                (response) => {
                    const fullKey = response?.data?.value;
                    if (!response?.status || !fullKey) {
                        handleOperationError(
                            response || { message: "Unable to view API Key." },
                            type
                        );
                        return;
                    }

                    const updateState = (previous) => ({
                        ...previous,
                        isSeen: true,
                        isRevealed: true,
                        valueFullTemporary: fullKey,
                    });

                    if (type === "main") {
                        setMainKeyStatus(updateState);
                    } else {
                        setAltKeyStatus(updateState);
                    }
                    setKeyActionLoading(type, null);

                    navigator.clipboard
                        .writeText(fullKey)
                        .then(() => {
                            enqueueSnackbar("API Key copied to clipboard", {
                                variant: "success",
                            });
                        })
                        .catch(() => {
                            enqueueSnackbar("Failed to copy API Key", {
                                variant: "error",
                            });
                        });
                },
                (error) => handleOperationError(error, type)
            )
        );
    };


    // Handler: Initiate Regenerate (shows confirmation)
    const handleRegenerate = (type) => {
        setConfirmAction(type);
        setConfirmOpen(true);
    };

    // Handler: Confirm Regenerate
    const handleConfirmRegenerate = () => {
        setConfirmOpen(false);
        const type = confirmAction;
        if (!type) {
            return;
        }

        setKeyActionLoading(type, "regenerate");
        dispatch(
            regenerateApiKeyApiCall(
                { apiKeyType: type, businessId: Bussines_id },
                (response) => {
                    updateSingleKeyState(type, response);
                    setKeyActionLoading(type, null);
                    enqueueSnackbar("API Key regenerated.", { variant: "success" });
                },
                (error) => handleOperationError(error, type)
            )
        );

        setConfirmAction(null);
    };

    // Handler: Cancel Regenerate confirmation
    const handleCancelRegenerate = () => {
        setConfirmOpen(false);
        setConfirmAction(null);
    };

    // Cleanup on modal close/unmount
    const handleModalClose = () => {
        // Clear all temporary full-key values
        setMainKeyStatus((prev) => ({
            ...prev,
            valueFullTemporary: "",
            isRevealed: false,
        }));
        setAltKeyStatus((prev) => ({
            ...prev,
            valueFullTemporary: "",
            isRevealed: false,
        }));
        onClose();
    };

    // Render a single key section
    const renderKeySection = (type, keyState) => {
        const typeLabel = type === "main" ? "Main API Key" : "Alternate API Key";

        return (
            <Card key={type} className={classes.keyCard}>
                <CardContent className={classes.cardContent}>
                    <Typography className={classes.cardHeader}>
                        {typeLabel}
                    </Typography>

                    {!keyState.exists ? (
                        <Box>
                            <Typography className={classes.emptyState}>
                                No key generated yet.
                            </Typography>
                            <Box className={classes.buttonGroup}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    className={classes.button}
                                    onClick={() => handleGenerate(type)}
                                    disabled={keyLoading[type] !== null}
                                >
                                    {keyLoading[type] === "generate" ? "Generate..." : "Generate"}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Box
                                className={`${classes.keyDisplay} ${keyState.isRevealed ? classes.keyDisplayRevealed : ""
                                    }`}
                            >
                                {keyState.isRevealed ? (
                                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                                        {keyState.valueFullTemporary || "••••••••••••••••"}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" className={classes.emptyState}>
                                        {keyState.valueRedacted || "••••••••••••••••"}
                                    </Typography>
                                )}
                            </Box>

                            <Box className={classes.buttonGroup}>
                                {keyState.isRevealed && keyState.valueFullTemporary ? (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            className={classes.button}
                                            onClick={() => handleRegenerate(type)}
                                            disabled={keyLoading[type] !== null}
                                        >
                                            {keyLoading[type] === "regenerate" ? "Regenerate..." : "Regenerate"}
                                        </Button>
                                    </>
                                ) : keyState.isSeen ? (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        className={classes.button}
                                        onClick={() => handleRegenerate(type)}
                                        disabled={keyLoading[type] !== null}
                                    >
                                        {keyLoading[type] === "regenerate" ? "Regenerate..." : "Regenerate"}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        className={classes.button}
                                        onClick={() => handleView(type)}
                                        disabled={keyLoading[type] !== null}
                                    >
                                        {keyLoading[type] === "view" ? "View / Copy..." : "View / Copy"}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <>
            <AppLoader loader={loading} />

            <Modal open={open} onClose={handleModalClose}>
                <div className={classes.paper}>
                    {/* Header */}
                    <div className={classes.header}>
                        <Typography className={classes.title}>API Key Management</Typography>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleModalClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    {/* Main API Key Section */}
                    {renderKeySection("main", mainKeyStatus)}

                    {/* Alternate API Key Section */}
                    {renderKeySection("alternate", altKeyStatus)}
                </div>
            </Modal>

            {/* Confirmation Dialog for Regenerate */}
            <Dialog
                open={confirmOpen}
                onClose={handleCancelRegenerate}
                className={classes.confirmDialog}
            >
                <DialogTitle>Confirm Regeneration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <span className={classes.warningText}>
                            Warning: Are you sure you want to regenerate?
                        </span>
                    </DialogContentText>
                    <DialogContentText style={{ marginTop: "16px" }}>
                        The existing API key will no longer be valid. The new key will be
                        shown only once.                    </DialogContentText>
                    <DialogContentText style={{ marginTop: "16px" }}>
                        Do you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelRegenerate}
                        color="primary"
                        className={classes.button}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRegenerate}
                        color="secondary"
                        variant="contained"
                        className={classes.button}
                    >
                        Regenerate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

ApiKeyModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    Bussines_id: PropTypes.string,
};

ApiKeyModal.defaultProps = {
    Bussines_id: null,
};

export default connect(null, {})(ApiKeyModal);
